/**
 * AdvisorNonCompliantModal
 *
 * Single shared modal that pops when the advisor clicks a red
 * (non-compliant) chip anywhere in the contacts table. Shows:
 *
 *   1. Who flagged it, when, and on which draft for which contact.
 *   2. The reviewer's note — the actionable part the advisor needs to
 *      read.
 *   3. An inline line-level diff of subject + body between:
 *        `reviewed_*`  (the snapshot the reviewer signed against; stored
 *                       inside the decision payload by the reviews API)
 *        `current_*`   (the current resolved subject/body the advisor's
 *                       table is displaying right now)
 *      so the advisor can see, at a glance, whether their own edits
 *      since the verdict changed anything the reviewer was objecting to.
 *
 * Controlled entirely through `modalContext` from `useAdvisorCompliance`:
 * null → closed; populated object → open with that pair's data. This
 * matches the hook's state shape and avoids parallel state flags.
 */

import React from 'react';
import {
  Badge,
  Box,
  Divider,
  HStack,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { AlertOctagon } from 'lucide-react';
import ComplianceContentDiff from './ComplianceContentDiff';

const formatTimestamp = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const AdvisorNonCompliantModal = ({ context, onClose }) => {
  const isOpen = Boolean(context);
  const decision = context?.decision || null;
  const flaggedBy = decision?.reviewer_display_name || 'A reviewer';
  const flaggedAt = formatTimestamp(decision?.updated_at);
  const note = decision?.note?.trim();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" isCentered>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent
        bg="#0a0a0a"
        color="#fafafa"
        border="1px solid rgba(255,255,255,0.10)"
        borderRadius="12px"
      >
        <ModalHeader
          pb={3}
          borderBottomWidth="1px"
          borderColor="rgba(255,255,255,0.08)"
        >
          <HStack spacing={2.5} align="center">
            <Box
              as="span"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              w={8}
              h={8}
              borderRadius="8px"
              bg="rgba(239,68,68,0.14)"
              color="#EF4444"
              border="1px solid"
              borderColor="rgba(239,68,68,0.30)"
              flexShrink={0}
            >
              <AlertOctagon size={16} strokeWidth={2.25} />
            </Box>
            <Stack spacing={0}>
              <Heading as="h3" fontSize="md" fontWeight="600" letterSpacing="-0.01em">
                Flagged non-compliant
              </Heading>
              {context?.contactName || context?.draftName ? (
                <Text fontSize="xs" color="rgba(255,255,255,0.55)">
                  {[context?.draftName, context?.contactName].filter(Boolean).join(' \u2022 ')}
                </Text>
              ) : null}
            </Stack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton
          color="#a3a3a3"
          _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
          _focus={{ boxShadow: 'none' }}
        />
        <ModalBody py={5}>
          <Stack spacing={5}>
            <HStack spacing={2} align="center" fontSize="xs" color="rgba(255,255,255,0.6)">
              <Text as="span">Flagged by</Text>
              <Text as="span" color="rgba(255,255,255,0.88)" fontWeight="500">
                {flaggedBy}
              </Text>
              {flaggedAt ? (
                <>
                  <Text as="span">on</Text>
                  <Text as="span" color="rgba(255,255,255,0.88)" fontWeight="500">
                    {flaggedAt}
                  </Text>
                </>
              ) : null}
              {decision?.is_stale ? (
                <Badge
                  ml={1}
                  bg="rgba(251,191,36,0.10)"
                  color="#FBBF24"
                  border="1px solid"
                  borderColor="rgba(251,191,36,0.35)"
                  fontSize="10px"
                  textTransform="none"
                  fontWeight="500"
                  px={1.5}
                  borderRadius="full"
                >
                  Content changed since review
                </Badge>
              ) : null}
            </HStack>

            <Box
              bg="rgba(239,68,68,0.06)"
              border="1px solid"
              borderColor="rgba(239,68,68,0.22)"
              borderRadius="8px"
              px={4}
              py={3}
            >
              <Text
                fontSize="11px"
                letterSpacing="0.04em"
                textTransform="uppercase"
                fontWeight="600"
                color="#fca5a5"
                mb={1.5}
              >
                Reviewer note
              </Text>
              <Text
                fontSize="sm"
                color="rgba(255,255,255,0.92)"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
              >
                {note || 'No note provided.'}
              </Text>
            </Box>

            <Divider borderColor="rgba(255,255,255,0.08)" />

            <ComplianceContentDiff
              title="Subject — what the reviewer saw vs. now"
              beforeText={decision?.reviewed_subject}
              afterText={context?.currentSubject}
            />
            <ComplianceContentDiff
              title="Body — what the reviewer saw vs. now"
              beforeText={decision?.reviewed_body}
              afterText={context?.currentBody}
            />
          </Stack>
        </ModalBody>
        {/*
          Deliberately no ModalFooter: the ModalCloseButton in the header
          already owns the dismiss affordance. A second "Close" button in
          the footer adds weight without a second job to do.
        */}
      </ModalContent>
    </Modal>
  );
};

export default AdvisorNonCompliantModal;
